import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import { generateFormObject } from "@/core/tools/forms/generate_form_object";
import moment from "moment";
import { prepareFormObjectForSave } from "@/core/tools/forms/prepare_form_object_for_save";

interface PrimeCostsList {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  schema: any;
  documents: any[];
}

const state: PrimeCostsList = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0,
  },

  schema: null,
  documents: [],
};

export const ITEMS_LIST = "GET_CITY_LIST";

export const actions: ActionTree<PrimeCostsList, RootState> = {
  startLoading({ commit }) {
    commit("START_LOADING");
  },
  loadedSuccess({ commit }) {
    commit("LOADED_SUCCESS");
  },
  loadData({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      const columns: any = {
        id: "prime_costs.id",
        product_id: "products.id",
        name: "products.name",
        cost: "prime_costs.cost",
        month: "prime_costs.month",
        container_name: "product_containers.name",
        sku_mixcart: "product_containers.sku_mixcart",
        retail_price: "prime_costs.retail_price",
        trade_price: "prime_costs.trade_price",
        product_archived_at: "products.archived_at",
        container_archived_at: "product_containers.archived_at",
      };

      const filter: any[] = [];

      if (query.name) {
        filter.push({ $ilike: ["products.name", { $: `%${query.name}%` }] });
      }
      //   filter.push({ $gt: ["prime_costs.cost", { $: "0" }] });
      if (query.costPriceDate) {
        filter.push({
          $eq: [
            "prime_costs.month",
            { $: moment(query.costPriceDate).format("YYYY-MM-DD") },
          ],
        });
      } else {
        filter.push({
          $eq: ["prime_costs.month", { $: moment().format("YYYY-MM-01") }],
        });
      }
      if (query.showArchived !== "true") {
        filter.push({
          $and: [
            { $null: ["products.archived_at"] },
            { $null: ["product_containers.archived_at"] },
          ],
        });
      }

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action({
            columns: columns,
            from: "s:prime_costs",
            from_as: "prime_costs",
            joins: [
              {
                join: "s:product_containers",
                as: "product_containers",
                on: {
                  $eq: [
                    "product_containers.id",
                    "prime_costs.product_container_id",
                  ],
                },
              },
              {
                join: "s:products",
                as: "products",
                on: {
                  $eq: ["products.id", "product_containers.product_id"],
                },
              },
            ],
            page_size: 10000,
            ...{ page: parseInt(query.page) || 1 },
            ...{
              order: [
                query.sortDirection == "desc"
                  ? { $desc: [columns[query.sortField || "name"]] }
                  : { $asc: [columns[query.sortField || "name"]] },
                { $asc: ["product_containers.name"] },
              ],
            },
            ...(filter.length > 1
              ? {
                  where: {
                    $and: filter,
                  },
                }
              : {
                  where: filter[0],
                }),
          }),
        vm
          .$q("GET_SCHEMA")
          .raw("schemas.get")
          .action({
            id: `s:prime_costs`,
          }),
      ]).then((result: ServerResult) => {
        commit("SET_ITEMS_LIST", {
          documents: result.getOperation(ITEMS_LIST).getContent().documents,
          meta: result.getOperation(ITEMS_LIST).getContent().meta,
          row_schema: result.getOperation(ITEMS_LIST).getContent().schema,
          schema: result.getOperation("GET_SCHEMA").getContent().schema,
        });
        commit("LOADED_SUCCESS");

        resolve(null);
      });
    });
  },

  changeValue({ commit }, { id, field, newValue }) {
    commit("SET_VALUE", { id, field, newValue });
  },

  savePrimeCost({ commit }, { id }) {
    const vm = (this as any)._vm;

    const document = state.documents.find((item: any) => item.id === id);
    if (!document) {
      return Promise.reject(new Error(`prime_cost id=${id} не найден`));
    }

    const normalizedForm = prepareFormObjectForSave(state.schema, document);

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      let saveQuery = null;

      saveQuery = vm.$q("DATA_SAVE").update(normalizedForm.id, normalizedForm);

      vm.$db([saveQuery])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("DATA_SAVE");

          if (operationResult.hasError()) {
            console.log("error", operationResult.getContent());

            commit("LOADED_SUCCESS");
            reject();
          } else {
            commit("LOADED_SUCCESS");
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },

  setPrimeCost({ commit }, data) {
    const vm = (this as any)._vm;

    const query = data.id
      ? vm.$q("SAVE_PRIME_COST").update(data.id, data)
      : vm.$q("SAVE_PRIME_COST").create("prime_costs", data);

    return new Promise((resolve, reject) => {
      vm.$db([query])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("SAVE_PRIME_COST");

          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },
};

export const mutations: MutationTree<PrimeCostsList> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ITEMS_LIST(state, data) {
    state.documents = data.documents.map((item: any) => {
      const document = generateFormObject(data.row_schema, item);
      document.edited = false;
      return document;
    });
    state.schema = data.schema;

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages,
    };
  },

  SET_VALUE(state, { id, field, newValue }) {
    const document = state.documents.find((item: any) => item.id === id);
    if (!document) return;

    document[field] = newValue;
    document.edited = true;
  },
};

const getters = {
  groupedDocuments(state: PrimeCostsList) {
    const groups: any = {};

    state.documents.forEach((item: any) => {
      const key = item.product_id;
      if (!groups[key]) {
        groups[key] = {
          id: item.product_id,
          name: item.name,
          archived_at: item.product_archived_at,
          containers: [],
        };
      }
      groups[key].containers.push(item);
    });

    const result: any[] = Object.values(groups);

    result.forEach((group: any) => {
      group.containers.sort((a: any, b: any) =>
        String(a.container_name || "").localeCompare(
          String(b.container_name || ""),
          "ru"
        )
      );
    });

    result.sort((a: any, b: any) =>
      String(a.name || "").localeCompare(String(b.name || ""), "ru")
    );

    return result;
  },

  exportData(state: PrimeCostsList) {
    const csvContent: any[] = [];
    const header = [
      "Продукт",
      "Наименование",
      "Код 1С",
      "Себестоимость, за 1 кг",
      "Рекомендуемая розничная цена",
      "Рекомендуемая оптовая цена",
    ];

    state.documents.forEach((item: any) => {
      csvContent.push([
        item.name,
        item.container_name,
        item.sku_mixcart,
        item.cost,
        item.retail_price,
        item.trade_price,
      ]);
    });

    return [header, ...csvContent];
  },
};

export const STORE_KEY = "$_prime_costs_list";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
